/**
 * useUndoRedo Hook
 * Manages undo/redo history for canvas changes
 * - Debounced save to avoid full graph serialization on every property change
 * - Batches rapid changes (e.g., dragging) into a single history entry
 */

import { useEffect, useRef, useCallback } from 'react'
import { dia } from '@joint/core'

const DEBOUNCE_MS = 300

interface UseUndoRedoReturn {
  undo: () => void
  redo: () => void
  checkpoint: () => void
}

export function useUndoRedo(
  graph: dia.Graph | null,
  onElementCountChange?: (count: number) => void,
): UseUndoRedoReturn {
  const historyStack = useRef<string[]>([])
  const historyIndex = useRef<number>(-1)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRestoring = useRef(false)

  // Save graph state to history (called after debounce)
  const commitState = useCallback(() => {
    if (!graph || isRestoring.current) return
    const state = JSON.stringify(graph.toJSON())

    // Skip if state is identical to current
    if (historyIndex.current >= 0 && historyStack.current[historyIndex.current] === state) {
      return
    }

    // Remove future states if we're not at the end
    historyStack.current = historyStack.current.slice(0, historyIndex.current + 1)

    // Add new state
    historyStack.current.push(state)
    historyIndex.current++

    // Limit history to 20 states
    if (historyStack.current.length > 20) {
      historyStack.current.shift()
      historyIndex.current--
    }
  }, [graph])

  // Debounced change handler — batches rapid changes into one history entry
  const handleChange = useCallback(() => {
    if (isRestoring.current) return
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(commitState, DEBOUNCE_MS)
  }, [commitState])

  // Track changes for undo/redo
  useEffect(() => {
    if (!graph) return

    // Save initial state
    commitState()

    graph.on('change', handleChange)
    graph.on('add', handleChange)
    graph.on('remove', handleChange)

    return () => {
      graph.off('change', handleChange)
      graph.off('add', handleChange)
      graph.off('remove', handleChange)
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [graph, handleChange, commitState])

  const undo = useCallback(() => {
    if (historyIndex.current > 0 && graph) {
      // Flush any pending debounced save before undo
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
        commitState()
      }

      historyIndex.current--
      const state = historyStack.current[historyIndex.current]
      isRestoring.current = true
      graph.fromJSON(JSON.parse(state))
      isRestoring.current = false

      if (onElementCountChange) {
        onElementCountChange(graph.getElements().length)
      }
    }
  }, [graph, commitState, onElementCountChange])

  const redo = useCallback(() => {
    if (historyIndex.current < historyStack.current.length - 1 && graph) {
      historyIndex.current++
      const state = historyStack.current[historyIndex.current]
      isRestoring.current = true
      graph.fromJSON(JSON.parse(state))
      isRestoring.current = false

      if (onElementCountChange) {
        onElementCountChange(graph.getElements().length)
      }
    }
  }, [graph, onElementCountChange])

  // Explicit boundaries keep bulk operations separate from pending edits.
  const checkpoint = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }
    commitState()
  }, [commitState])

  return { undo, redo, checkpoint }
}
